import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

import NavigationSidebar from 'components/ui/NavigationSidebar';
import JobCard from './components/JobCard';
import JobPreviewPanel from './components/JobPreviewPanel';
import { supabase } from '@/lib/supabase';

const JobSearchBrowse = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedFilters, setSelectedFilters] = useState({
    location: [],
    fitScore: 0,
    likelihoodOfInterview: '',
    graduateReadiness: [],
    skillMatch: '',
    remoteHybrid: [],
    visaSupport: '',
    timeToOffer: ''
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recentSearches] = useState(['React Developer', 'Product Manager', 'UX Designer']);
  const [searchSuggestions] = useState(['Frontend Developer', 'Backend Engineer', 'Full Stack Developer', 'DevOps Engineer']);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // No more mock login state

  const ITEMS_PER_PAGE = 20;
  const searchInputRef = useRef(null);
  // Removed observerRef for pagination

  // Mock job data
  const mockJobs = [
    {
      id: '1',
      title: 'Senior React Developer',
      company: 'TechCorp Solutions',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=64&h=64&fit=crop&crop=center',
      location: 'San Francisco, CA',
      employmentType: 'Full-time',
      salaryRange: '$120,000 - $160,000',
      postedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      description: `We're looking for a Senior React Developer to join our dynamic team. You'll be responsible for building scalable web applications using modern React patterns and best practices.

Key responsibilities include developing user interfaces, optimizing application performance, and collaborating with cross-functional teams to deliver high-quality software solutions.`,
      requirements: ['5+ years React experience', 'TypeScript proficiency', 'Redux/Context API', 'Testing frameworks'],
      companySize: '100-500',
      experienceLevel: 'Senior',
      remoteWork: true,
      featured: true
    },
    {
      id: '2',
      title: 'Product Manager',
      company: 'InnovateLabs',
      logo: 'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=64&h=64&fit=crop&crop=center',
      location: 'New York, NY',
      employmentType: 'Full-time',
      salaryRange: '$130,000 - $170,000',
      postedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      description: `Join our product team as a Product Manager where you'll drive product strategy and execution for our flagship SaaS platform.

You'll work closely with engineering, design, and marketing teams to define product roadmaps, gather user feedback, and ensure successful product launches.`,
      requirements: ['3+ years product management', 'Agile methodologies', 'Data analysis skills', 'User research experience'],
      companySize: '50-100',
      experienceLevel: 'Mid-level',
      remoteWork: false,
      featured: false
    },
    {
      id: '3',
      title: 'UX/UI Designer',
      company: 'DesignStudio Pro',
      logo: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=64&h=64&fit=crop&crop=center',
      location: 'Austin, TX',
      employmentType: 'Contract',
      salaryRange: '$80,000 - $110,000',
      postedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      description: `We're seeking a talented UX/UI Designer to create intuitive and engaging user experiences for our digital products.

You'll be responsible for conducting user research, creating wireframes and prototypes, and collaborating with development teams to implement designs.`,
      requirements: ['4+ years UX/UI design', 'Figma/Sketch proficiency', 'User research skills', 'Prototyping experience'],
      companySize: '10-50',
      experienceLevel: 'Mid-level',
      remoteWork: true,
      featured: false
    },
    {
      id: '4',
      title: 'DevOps Engineer',
      company: 'CloudTech Systems',
      logo: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=64&h=64&fit=crop&crop=center',
      location: 'Seattle, WA',
      employmentType: 'Full-time',
      salaryRange: '$140,000 - $180,000',
      postedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
      description: `Join our infrastructure team as a DevOps Engineer to help scale our cloud-native applications and improve deployment processes.

You'll work with containerization, CI/CD pipelines, and cloud platforms to ensure reliable and efficient software delivery.`,
      requirements: ['AWS/Azure experience', 'Docker/Kubernetes', 'CI/CD pipelines', 'Infrastructure as Code'],
      companySize: '200-500', experienceLevel: 'Senior',
      remoteWork: true,
      featured: true
    },
    {
      id: '5', title: 'Data Scientist', company: 'Analytics Plus', logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=64&h=64&fit=crop&crop=center', location: 'Boston, MA', employmentType: 'Full-time', salaryRange: '$110,000 - $150,000', postedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), description: `We're looking for a Data Scientist to join our analytics team and help drive data-driven decision making across the organization.

You'll work with large datasets, build predictive models, and create insights that directly impact business strategy and product development.`,
      requirements: ['Python/R proficiency', 'Machine learning', 'SQL expertise', 'Statistical analysis'],
      companySize: '100-200', experienceLevel: 'Mid-level',
      remoteWork: false,
      featured: false
    },
    {
      id: '6', title: 'Frontend Developer', company: 'WebCraft Agency', logo: 'https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=64&h=64&fit=crop&crop=center', location: 'Los Angeles, CA', employmentType: 'Part-time', salaryRange: '$60,000 - $80,000',
      postedDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
      description: `Join our creative team as a Frontend Developer to build beautiful and responsive web applications for our diverse client base.

You'll work with modern JavaScript frameworks and collaborate with designers to bring creative visions to life.`,
      requirements: ['3+ years frontend development', 'React/Vue.js', 'CSS/SASS', 'Responsive design'],
      companySize: '10-50',
      experienceLevel: 'Mid-level',
      remoteWork: true,
      featured: false
    },
    {
      id: '7',
      title: 'Backend Engineer',
      company: 'ServerTech Solutions',
      logo: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=64&h=64&fit=crop&crop=center',
      location: 'Chicago, IL',
      employmentType: 'Full-time',
      salaryRange: '£125,000 - £165,000',
      postedDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      description: `We're seeking a Backend Engineer to design and implement scalable server-side applications and APIs. You'll work with microservices architecture, databases, and cloud platforms to build robust backend systems that power our applications.`,
      requirements: ['Node.js/Python/Java', 'Database design', 'API development', 'Microservices'],
      companySize: '50-100',
      experienceLevel: 'Senior',
      remoteWork: true,
      featured: false
    },
    {
      id: '8',
      title: 'Marketing Manager',
      company: 'GrowthCo',
      logo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=center',
      location: 'Miami, FL',
      employmentType: 'Full-time',
      salaryRange: '£90,000 - £120,000',
      postedDate: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
      description: `Join our marketing team as a Marketing Manager to develop and execute comprehensive marketing strategies that drive growth and brand awareness.

You'll manage campaigns across multiple channels, analyze performance metrics, and collaborate with cross-functional teams to achieve business objectives.`,
      requirements: ['5+ years marketing experience', 'Digital marketing', 'Analytics tools', 'Campaign management'],
      companySize: '100-200', experienceLevel: 'Senior',
      remoteWork: false,
      featured: false
    }
  ];

  const [filteredJobs, setFilteredJobs] = useState(mockJobs);
  const [displayedJobs, setDisplayedJobs] = useState(mockJobs?.slice(0, ITEMS_PER_PAGE));

  // Transform data to match component expectations
  const transformJobData = (job) => ({
    ...job,
    company: job.companies?.name || 'Unknown Company',
    logo: job.companies?.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(job.companies?.name || 'Job')}&background=random&color=fff&size=128`,
    postedDate: new Date(job.posted_at),
    salaryRange: `£${(job.salary_min / 1000).toFixed(0)}k - £${(job.salary_max / 1000).toFixed(0)}k`
  });

  // Sync URL with Search Query changes (optional debounced or effect, but here initialized is mostly important)
  useEffect(() => {
    const query = searchParams.get('q');
    if (query !== null && query !== searchQuery) {
      setSearchQuery(query);
    }
  }, [searchParams]);

  // Fetch jobs from Supabase
  useEffect(() => {
    const fetchJobs = async () => {
      setIsLoading(true);
      try {
        // Initialize query with appropriate select string based on employer filter
        let selectString = '*, companies(*)';
        if (selectedFilters?.employer?.trim()) {
          selectString = '*, companies!inner(*)';
        }

        let query = supabase.from('jobs').select(selectString);

        // Search query filter
        if (searchQuery?.trim()) {
          query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
        }
        if (selectedFilters?.keywords?.trim()) {
          const keywordExpansions = {
            'software': ['software', 'developer', 'engineer', 'java', 'javascript', 'react', 'python', 'node', 'sql', 'html', 'css', 'c++', 'c#', '.net', 'typescript', 'aws', 'cloud'],
            'tech': ['software', 'technology', 'it', 'developer', 'engineer', 'system', 'network'],
            'marketing': ['marketing', 'seo', 'social media', 'content', 'brand', 'pr', 'copywriting', 'advertising'],
            'sales': ['sales', 'business development', 'account manager', 'sales executive'],
            'design': ['design', 'graphic', 'ui', 'ux', 'creative', 'art', 'web design', 'illustrator', 'photoshop'],
            'finance': ['finance', 'accounting', 'analyst', 'audit', 'tax', 'banking', 'wealth'],
            'hr': ['hr', 'human resources', 'recruitment', 'talent', 'people'],
            'admin': ['admin', 'administrator', 'office', 'assistant', 'receptionist', 'clerk']
          };

          const terms = selectedFilters.keywords.trim().toLowerCase().split(/\s+/);
          const expandedTerms = new Set();

          terms.forEach(term => {
            expandedTerms.add(term);
            if (keywordExpansions[term]) {
              keywordExpansions[term].forEach(exp => expandedTerms.add(exp));
            }
          });

          const termConditions = Array.from(expandedTerms).map(term => `title.ilike.%${term}%,description.ilike.%${term}%`).join(',');
          query = query.or(termConditions);
        }

        if (selectedFilters.location.length > 0) {
          // Region to Keyword Mapping for robust "Best Match"
          const regionKeywords = {
            'London': ['London'],
            'South East': ['South East', 'Kent', 'Essex', 'Surrey', 'Sussex', 'Hampshire', 'Berkshire', 'Oxfordshire', 'Buckinghamshire', 'Brighton', 'Southampton', 'Portsmouth', 'Reading', 'Milton Keynes'],
            'South West': ['South West', 'Bristol', 'Cornwall', 'Devon', 'Dorset', 'Somerset', 'Wiltshire', 'Gloucestershire', 'Exeter', 'Plymouth', 'Bath'],
            'Midlands': ['Midland', 'Birmingham', 'Coventry', 'Leicester', 'Nottingham', 'Derby', 'Stoke', 'Wolverhampton', 'Northampton', 'Lincoln'],
            'North West': ['North West', 'Manchester', 'Liverpool', 'Lancashire', 'Cumbria', 'Cheshire', 'Warrington', 'Blackpool', 'Preston'],
            'North East': ['North East', 'Newcastle', 'Sunderland', 'Durham', 'Northumberland', 'Middlesbrough'],
            'Yorkshire': ['Yorkshire', 'Leeds', 'Sheffield', 'Bradford', 'Hull', 'York'],
            'Scotland': ['Scotland', 'Glasgow', 'Edinburgh', 'Aberdeen', 'Dundee'],
            'Wales': ['Wales', 'Cardiff', 'Swansea', 'Newport'],
            'Northern Ireland': ['Northern Ireland', 'Belfast', 'Derry', 'Antrim'],
            'Remote': ['Remote', 'Home Based', 'Work from home']
          };

          const conditions = [];

          selectedFilters.location.forEach(loc => {
            if (loc === 'Remote') {
              // Handle Remote specially: Check is_remote flag OR text match
              // Note: mixing AND/OR logic in Supabase query builder can be tricky. 
              // For now, we'll treat it as a text match on location OR is_remote=true check later?
              // Actually, simpler to just match common remote strings in location for consistency here.
              // The specific 'Remote' checkbox in sidebar handles the is_remote column strictly.
              conditions.push('is_remote.eq.true'); // Check boolean column
              conditions.push('location.ilike.%Remote%');
            } else {
              const keywords = regionKeywords[loc] || [loc];
              keywords.forEach(kw => {
                conditions.push(`location.ilike.%${kw}%`);
              });
            }
          });

          if (conditions.length > 0) {
            query = query.or(conditions.join(','));
          }
        }

        // --- IMPROVED REMOTE / HYBRID LOGIC ---
        if (selectedFilters.remoteHybrid && selectedFilters.remoteHybrid.length > 0) {
          const remoteConditions = [];

          if (selectedFilters.remoteHybrid.includes('Remote')) {
            remoteConditions.push('is_remote.eq.true');
            remoteConditions.push('location.ilike.%Remote%');
          }
          if (selectedFilters.remoteHybrid.includes('Hybrid')) {
            remoteConditions.push('description.ilike.%hybrid%');
            remoteConditions.push('title.ilike.%hybrid%');
          }
          if (selectedFilters.remoteHybrid.includes('On-site')) {
            remoteConditions.push('is_remote.eq.false');
          }

          if (remoteConditions.length > 0) {
            query = query.or(remoteConditions.join(','));
          }
        }

        // Employer Filter Application
        if (selectedFilters?.employer?.trim()) {
          query = query.ilike('companies.name', `%${selectedFilters.employer}%`);
        }

        // Salary Min
        if (selectedFilters?.minSalary) {
          query = query.gte('salary_max', selectedFilters.minSalary);
        }

        // Salary Max
        if (selectedFilters?.maxSalary) {
          query = query.lte('salary_min', selectedFilters.maxSalary);
        }

        // Role Type (Search Title)
        if (selectedFilters?.roleType?.trim()) {
          query = query.ilike('title', `%${selectedFilters.roleType}%`);
        }

        // --- IMPROVED SECTOR LOGIC ---
        if (selectedFilters?.sector?.trim()) {
          const sectorMap = {
            'Technology & Software': ['software engineer', 'software developer', 'web developer', 'frontend', 'backend', 'full stack', 'devops', 'cloud engineer', 'data scientist', 'system administrator', 'network engineer', 'technical support', 'information technology', 'computer science'],
            'Finance & Banking': ['finance', 'banking', 'accountant', 'analyst', 'tax', 'audit', 'investment', 'payroll', 'wealth management'],
            'Consulting': ['consultant', 'consulting', 'strategy', 'advisory', 'business analyst'],
            'Healthcare & Life Sciences': ['healthcare', 'medical', 'nursing', 'pharmacy', 'clinical', 'biomedical', 'laboratory', 'doctor', 'physiotherapist'],
            'Engineering & Manufacturing': ['mechanical engineer', 'electrical engineer', 'civil engineer', 'manufacturing', 'production', 'maintenance engineer', 'structural engineer'],
            'Digital Marketing': ['digital marketing', 'seo', 'content marketing', 'social media manager', 'brand manager', 'marketing executive', 'copywriter'],
            'Data Science & Analytics': ['data analyst', 'business intelligence', 'machine learning', 'data engineering', 'analytics'],
            'Creative & Design': ['graphic designer', 'ux designer', 'ui designer', 'product designer', 'art director', 'creative director', 'animator'],
            'Education': ['teacher', 'teaching assistant', 'tutor', 'lecturer', 'education', 'academic'],
            'Public Sector': ['public sector', 'civil service', 'local government', 'policy', 'council', 'nhs']
          };

          const keywords = sectorMap[selectedFilters.sector];
          if (keywords) {
            const sectorConditions = keywords.map(kw => `title.ilike.%${kw}%,description.ilike.%${kw}%`).join(',');
            query = query.or(sectorConditions);
          } else {
            query = query.or(`title.ilike.%${selectedFilters.sector}%,description.ilike.%${selectedFilters.sector}%`);
          }
        }

        // Sorting
        if (sortBy === 'date') {
          query = query.order('posted_at', { ascending: false });
        } else if (sortBy === 'salary') {
          query = query.order('salary_max', { ascending: false });
        } else {
          query = query.order('featured', { ascending: false }).order('posted_at', { ascending: false });
        }

        const { data, error } = await query;
        if (error) throw error;

        const transformed = data.map(transformJobData);
        setFilteredJobs(transformed);
        setDisplayedJobs(transformed.slice(0, ITEMS_PER_PAGE));
        setHasMore(transformed.length > ITEMS_PER_PAGE);
        setCurrentPage(1);
      } catch (err) {
        console.error('Error fetching jobs:', err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, [searchQuery, selectedFilters, sortBy, user]);

  // Fetch saved job IDs for the current user
  useEffect(() => {
    const fetchSavedJobs = async () => {
      if (!user) {
        setSavedJobIds(new Set());
        return;
      }

      try {
        const { data, error } = await supabase
          .from('saved_jobs')
          .select('job_id');

        if (error) throw error;
        setSavedJobIds(new Set(data.map(item => item.job_id)));
      } catch (err) {
        console.error('Error fetching saved jobs:', err.message);
      }
    };

    fetchSavedJobs();
  }, [user]);

  // Handle Page Change
  useEffect(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedJobs(filteredJobs.slice(startIndex, endIndex));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, filteredJobs]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(filteredJobs.length / ITEMS_PER_PAGE)) {
      setCurrentPage(newPage);
    }
  };

  const totalPages = Math.ceil(filteredJobs?.length / ITEMS_PER_PAGE);


  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    setShowSuggestions(false);
  };

  const handleSaveJob = async (jobId) => {
    if (!user) {
      // Potentially redirect to login or show a toast
      navigate('/job-seeker-registration-login');
      return;
    }

    const isSaved = savedJobIds.has(jobId);

    try {
      if (isSaved) {
        // Remove from DB
        const { error } = await supabase
          .from('saved_jobs')
          .delete()
          .eq('user_id', user.id)
          .eq('job_id', jobId);

        if (error) throw error;

        setSavedJobIds(prev => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      } else {
        // Add to DB
        const { error } = await supabase
          .from('saved_jobs')
          .insert({ user_id: user.id, job_id: jobId });

        if (error) throw error;

        setSavedJobIds(prev => {
          const next = new Set(prev);
          next.add(jobId);
          return next;
        });
      }
    } catch (err) {
      console.error('Error toggling saved job:', err.message);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedFilters?.location?.length > 0) count++;
    if (user && selectedFilters?.fitScore > 0) count++;
    if (user && selectedFilters?.likelihoodOfInterview) count++;
    if (selectedFilters?.graduateReadiness?.length > 0) count++;
    if (user && selectedFilters?.skillMatch) count++;
    if (selectedFilters?.remoteHybrid?.length > 0) count++;
    if (selectedFilters?.visaSupport) count++;
    if (selectedFilters?.timeToOffer) count++;
    // New filters
    if (selectedFilters?.minSalary) count++;
    if (selectedFilters?.maxSalary) count++;
    if (selectedFilters?.keywords) count++;
    if (selectedFilters?.employer) count++;
    if (selectedFilters?.roleType) count++;
    if (selectedFilters?.sector) count++;
    if (searchQuery) count++; // Count active search
    return count;
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSearchParams({});
    setSelectedFilters({
      location: [],
      fitScore: 0,
      likelihoodOfInterview: '',
      graduateReadiness: [],
      skillMatch: '',
      remoteHybrid: [],
      visaSupport: '',
      timeToOffer: '',
      minSalary: '',
      maxSalary: '',
      keywords: '',
      employer: '',
      roleType: '',
      sector: ''
    });
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('q');
    setSearchParams(newParams);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-2xl mx-auto flex min-h-screen">
        {/* Navigation Sidebar with Filters */}
        <div className="flex-shrink-0 sticky top-20 md:top-24 h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)] overflow-hidden">
          <NavigationSidebar
            showFilters={true}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={clearAllFilters}
            activeFiltersCount={getActiveFiltersCount()}
            isLoggedIn={!!user}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 bg-background px-4 sm:px-6 lg:px-8 py-6">



          <div className="flex gap-6">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div className="flex items-center space-x-4">
                  <p className="text-text-secondary">
                    <span className="font-medium text-text-primary">{filteredJobs?.length}</span> jobs found
                  </p>

                  {getActiveFiltersCount() > 0 && (
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-background"
                  >
                    <option value="relevance">Most Relevant</option>
                    <option value="date">Most Recent</option>
                    <option value="salary">Highest Salary</option>
                  </select>
                </div>
              </div>

              {/* Active Filters */}
              {getActiveFiltersCount() > 0 && (
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {/* Search Query Chip */}
                    {searchQuery && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full font-medium">
                        <span>Search: {searchQuery}</span>
                        <button
                          onClick={handleClearSearch}
                          className="hover:bg-primary-200 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}
                    {selectedFilters?.location?.map((location) => (
                      <span
                        key={location}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full"
                      >
                        <span>{location}</span>
                        <button
                          onClick={() => handleFilterChange('location', selectedFilters?.location?.filter(l => l !== location))}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    ))}

                    {selectedFilters?.graduateReadiness?.map((level) => (
                      <span
                        key={level}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full"
                      >
                        <span>{level}</span>
                        <button
                          onClick={() => handleFilterChange('graduateReadiness', selectedFilters?.graduateReadiness?.filter(l => l !== level))}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    ))}

                    {selectedFilters?.remoteHybrid?.map((option) => (
                      <span
                        key={option}
                        className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full"
                      >
                        <span>{option}</span>
                        <button
                          onClick={() => handleFilterChange('remoteHybrid', selectedFilters?.remoteHybrid?.filter(o => o !== option))}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    ))}

                    {user && selectedFilters?.fitScore > 0 && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                        <span>Fit Score: {selectedFilters?.fitScore}%</span>
                        <button
                          onClick={() => handleFilterChange('fitScore', 0)}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}

                    {user && selectedFilters?.likelihoodOfInterview && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                        <span>Likelihood: {selectedFilters?.likelihoodOfInterview}</span>
                        <button
                          onClick={() => handleFilterChange('likelihoodOfInterview', '')}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}

                    {user && selectedFilters?.skillMatch && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                        <span>{selectedFilters?.skillMatch}</span>
                        <button
                          onClick={() => handleFilterChange('skillMatch', '')}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}

                    {selectedFilters?.visaSupport && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                        <span>{selectedFilters?.visaSupport}</span>
                        <button
                          onClick={() => handleFilterChange('visaSupport', '')}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}

                    {selectedFilters?.timeToOffer && (
                      <span className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                        <span>{selectedFilters?.timeToOffer}</span>
                        <button
                          onClick={() => handleFilterChange('timeToOffer', '')}
                          className="hover:bg-primary-100 rounded-full p-0.5"
                        >
                          <Icon name="X" size={12} />
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Job Listings */}
              <div className="space-y-4">
                {displayedJobs?.map((job) => (
                  <JobCard
                    key={job?.id}
                    job={job}
                    isSaved={savedJobIds?.has(job?.id)}
                    onSave={() => handleSaveJob(job?.id)}
                    onSelect={() => setSelectedJobId(job?.id)}
                    isSelected={selectedJobId === job?.id}
                    formatTimeAgo={formatTimeAgo}
                  />
                ))}

                {/* Loading Skeleton */}
                {isLoading && (
                  <div className="space-y-4">
                    {[...Array(3)]?.map((_, index) => (
                      <div key={index} className="bg-background border border-border rounded-lg p-6 animate-pulse">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-secondary-200 rounded-lg"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-secondary-200 rounded w-3/4"></div>
                            <div className="h-3 bg-secondary-200 rounded w-1/2"></div>
                            <div className="h-3 bg-secondary-200 rounded w-1/3"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination Controls */}
                {!isLoading && filteredJobs.length > 0 && (
                  <div className="flex items-center justify-center space-x-4 py-8 border-t border-border mt-8">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${currentPage === 1
                        ? 'border-border text-text-disabled cursor-not-allowed bg-secondary-50'
                        : 'border-border text-text-primary hover:bg-secondary-50 hover:text-primary-600'
                        }`}
                    >
                      <Icon name="ChevronLeft" size={20} />
                      <span>Previous</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      {/* Page Numbers */}
                      {(() => {
                        const maxVisiblePages = 7; // Adjust for how many buttons you want max
                        const pages = [];

                        if (totalPages <= maxVisiblePages) {
                          for (let i = 1; i <= totalPages; i++) pages.push(i);
                        } else {
                          // Always show first
                          pages.push(1);

                          if (currentPage > 3) {
                            pages.push('...');
                          }

                          let start = Math.max(2, currentPage - 1);
                          let end = Math.min(totalPages - 1, currentPage + 1);

                          // Adjust if close to start/end to keep count consistent-ish
                          if (currentPage <= 3) {
                            end = 4;
                          }
                          if (currentPage >= totalPages - 2) {
                            start = totalPages - 3;
                          }

                          for (let i = start; i <= end; i++) {
                            if (i > 1 && i < totalPages) pages.push(i);
                          }

                          if (currentPage < totalPages - 2) {
                            pages.push('...2'); // Unique key needed? Just string is fine for now, key handling below
                          }

                          // Always show last
                          pages.push(totalPages);
                        }

                        return pages.map((page, index) => {
                          if (typeof page === 'string') {
                            return (
                              <span key={`ellipsis-${index}`} className="px-2 text-text-secondary">...</span>
                            );
                          }

                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === page === 'string' ? '' : // Safe guard logic check? No page is number.
                                  currentPage === page
                                    ? 'bg-primary-600 text-white'
                                    : 'text-text-secondary hover:bg-secondary-50 hover:text-primary-600'
                                }`}
                            >
                              {page}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${currentPage === totalPages || totalPages === 0
                        ? 'border-border text-text-disabled cursor-not-allowed bg-secondary-50'
                        : 'border-border text-text-primary hover:bg-secondary-50 hover:text-primary-600'
                        }`}
                    >
                      <span>Next</span>
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>
                )}

                {/* No Results */}
                {filteredJobs?.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="Search" size={32} className="text-text-muted" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-primary mb-2">No jobs found</h3>
                    <p className="text-text-secondary mb-4">Try adjusting your search criteria or filters</p>
                    <button
                      onClick={clearAllFilters}
                      className="btn-primary"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Job Preview Panel - Desktop */}
            {selectedJobId && (
              <div className="hidden xl:block w-96 flex-shrink-0">
                <JobPreviewPanel
                  job={filteredJobs?.find(j => j?.id === selectedJobId)}
                  onClose={() => setSelectedJobId(null)}
                  isSaved={savedJobIds?.has(selectedJobId)}
                  onSave={() => handleSaveJob(selectedJobId)}
                  formatTimeAgo={formatTimeAgo}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSearchBrowse;